package games.polyclub.website.frontend.store

import axios.Axios
import axios.AxiosResponse
import games.polyclub.frontend.store.shared.*
import games.polyclub.message.Pronoun
import games.polyclub.service.config.api.SecretString
import games.polyclub.service.payment.api.Order
import games.polyclub.service.payment.api.OrderSummary
import games.polyclub.service.payment.api.id.Gateway
import games.polyclub.service.payment.api.id.OfferId
import games.polyclub.service.payment.api.id.OrderId
import games.polyclub.service.payment.api.id.ProductId
import games.polyclub.service.player.id.api.NetworkId
import games.polyclub.service.player.id.api.PlayerId
import games.polyclub.service.profile.api.PaymentProfile
import games.polyclub.service.store.api.ConsumableInventory
import games.polyclub.service.store.api.Offer
import games.polyclub.service.store.api.ShoppingCart
import games.polyclub.service.store.api.StoreOpenPayment
import games.polyclub.tool.endpoint.EndpointInvoker
import games.polyclub.tool.endpoint.GMson
import games.polyclub.tool.functional.Either
import games.polyclub.tool.functional.Option
import games.polyclub.tool.strings.id.UniqueId
import games.polyclub.tool.universal.Paginable
import io.ktor.client.call.*
import io.ktor.http.*
import io.ktor.utils.io.errors.*
import js.core.jso
import kotlinx.coroutines.async
import kotlinx.coroutines.await
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.first
import kotlinx.serialization.encodeToString
import kotlin.js.json

object StoreBff {
    private val _token = MutableStateFlow<String?>(null)
    internal var token: String?
        get() = _token.value
        set(value) {
            _token.value = value
        }

    private val axios = appScope.async {
        val url = storeBff.await().endpoint.baseUrl.toString()
        Axios.create(jso {
            baseURL = url
        })
    }

    inline fun <reified R: Any> axiosTransformer(): (Any) -> R? = { data ->
        if (data.toString().isBlank()) null
        else GMson.decodeFromString<R>(data.toString())
    }

    private suspend fun bff(): EndpointInvoker = storeBff.await()
    suspend fun authenticated(): EndpointInvoker = bff().also {
        _token.filter { it != null }.first()
        check(token != null) { "O usuário não está logado" }
    }
    suspend fun loadHCaptchaKey(): String = bff().get("hcaptcha-key")

    private fun AxiosResponse<LoginResponse>.handleLoginResponse(): Option<LoginResponse>? {
        val status = HttpStatusCode.fromValue(status.toInt())
        return when {
            status.isSuccess() -> Option.of(data)
            status == HttpStatusCode.Forbidden || status == HttpStatusCode.FailedDependency -> Option.empty()
            status == HttpStatusCode.Unauthorized -> null
            else -> throw IOException("O backend retornou um status inesperado $status")
        }
    }

    private fun <D: Any> ensureNotNull(data: D?): D = checkNotNull(data)

    private fun <R: Any> AxiosResponse<R>.handleNullableResponse(): R? {
        val status = HttpStatusCode.fromValue(status.toInt())
        return if (status == HttpStatusCode.UnprocessableEntity) {
            null
        } else ensureNotNull(data)
    }

    private fun <R: Any> AxiosResponse<R>.handleResponse(): R = checkNotNull(handleNullableResponse())

    suspend fun refreshAuthToken() = axios.await().get<LoginResponse>("/login/refresh", jso {
        withCredentials = true
        validateStatus = { it.toInt() < 500 }
        transformResponse = axiosTransformer<LoginResponse>()
    }).await().handleLoginResponse()

    suspend fun login(request: LoginRequest) =
        axios.await().post<LoginResponse>("/login", GMson.encodeToString(request), jso {
                headers = json("Content-Type" to "application/json")
                withCredentials = true
                validateStatus = { it.toInt() < 500 }
                transformResponse = axiosTransformer<LoginResponse>()
            }).await().handleLoginResponse()

    suspend fun logout() {
        axios.await()("/logout", jso {
            withCredentials = true
        }).await()
    }

    suspend fun playerIdByNick(nick: String): PlayerId? = bff().get("player-id", nick)

    suspend fun avatarUrl(networkId: NetworkId) = URLBuilder(authenticated().baseUrl).appendPathSegments("avatar", "$networkId.png", encodeSlash = true).toString()
    suspend fun avatarUrl(playerId: PlayerId) = avatarUrl(playerId.networkId)
    suspend fun avatarMojangUrl(mojangId: UniqueId) = URLBuilder(authenticated().baseUrl).appendPathSegments("avatar-mojang", "$mojangId.png", encodeSlash = true).toString()
    suspend fun register(request: RegisterRequest): RegisterResponse {
        return axios.await().post<RegisterResponse>("/register", GMson.encodeToString(request), jso {
            headers = json("Content-Type" to "application/json")
            withCredentials = true
            validateStatus = { it.toInt() < 500 }
            transformResponse = axiosTransformer<RegisterResponse>()
        }).await().handleResponse()
    }

    suspend fun registerPaymentProfile(request: RegisterPaymentProfileRequest): RegisterResponse {
        return axios.await().post<RegisterResponse>("/register-payment-profile", GMson.encodeToString(request), jso {
            headers = json("Content-Type" to "application/json")
            withCredentials = true
            validateStatus = { it.toInt() < 500 }
            transformResponse = axiosTransformer<RegisterResponse>()
        }).await().handleResponse()
    }

    suspend fun createOrder(request: ShoppingCart): CreateOrderResult = bff().post(request, "order", "create")
    suspend fun loadOffer(offerId: OfferId): Offer.Loaded? = bff().get("offer", offerId)
    suspend fun createInvoice(cart: ShoppingCart, gateway: Gateway): Either<OrderId, String> = bff().post(cart, "invoice", "create", gateway)
    suspend fun readInvoice(orderId: OrderId): Either<StoreOpenPayment, String> {
        val response = bff().get.fetchResponse("invoice", orderId)
        return when (val status = response.status) {
            HttpStatusCode.UnprocessableEntity -> Either.b("Pedido não encontrado")
            HttpStatusCode.Forbidden -> Either.b("Permissão negada")
            HttpStatusCode.Unauthorized -> Either.b("Login necessário")
            else -> when {
                status.isSuccess() -> Either.a(response.body())
                else -> Either.b("Ocorreu um erro ao processar o seu pedido de conexão :(")
            }
        }
    }

    suspend fun sandboxSimulatePayment(orderId: OrderId): Boolean = bff().get("invoice", "simulate", orderId)

    suspend fun readOriginalOrder(orderId: OrderId): Order? = bff().get("invoice", "order", orderId)
    suspend fun loadPaymentProfile(): Option<PaymentProfile> = bff().get("payment-profile")
    suspend fun deletePaymentProfile(): Unit = bff().delete("payment-profile")
    suspend fun listOrderSumaries(page: Int, itemsPerPage: Int): Paginable<OrderSummary> = bff().get("invoice", "list", itemsPerPage, page)
    suspend fun changePassword(current: SecretString, new: SecretString): String = bff().post(listOf(current, new), "change-password")
    suspend fun changePronoun(pronoun: Option<Pronoun>?): Unit = bff().post(Option.of(pronoun), "change-pronoun")
    suspend fun loadInventory(): ConsumableInventory = bff().get("inventory")
    suspend fun playerName(playerId: PlayerId): String = bff().post(playerId, "player-name")
    suspend fun productName(productId: ProductId): String = bff().get("product-name", productId)
}

